import React from "react";

// styles
import Styled from "./FareFamilies.styled";
import {
  COLOR_VARIANTS,
  Typography,
  TYPOGRAPHY_VARIANT,
} from "@tajawal/ct-web-design-system";

// components
import FareFamilyCard from "./components/FareFamilyCard/FareFamilyCard";
import FareFamilyCardSkeleton from "./components/FareFamilyCardSkeleton/FareFamilyCardSkeleton";
import { FareFamilyCardBasic } from "@/app-common/components/FareFamilyCardBasic";
import { CardCarousel } from "@/app-common/components/CardCarousel/CardCarousel";
import FareFamilyError from "./components/FareFamilyError/FareFamilyError";

// hooks
import { useStore } from "@/contexts/StoreContext";
import { useTranslation } from "react-i18next";
import useStateOfFareFamilies from "./hooks/useStateOfFareFamilies";
import useFetchFareFamilies from "./hooks/useFetchFareFamilies";
import useStateOfGrouping from "@/hooks/useStateOfGrouping";

// utils
import _map from "lodash/map";
import _isEmpty from "lodash/isEmpty";
import _findIndex from "lodash/findIndex";
import {
  pushFareFamilySelectedEvent,
  pushStepFlightSelectedEvent,
} from "@/utils/avoGtm.utils";

// types
import { Vendor } from "@tajawal/web-flights-sdk/lib/types/common.types";
import { ItineraryModel } from "@tajawal/web-flights-sdk/lib/types/itinerary.types";
import { GenericCallback } from "@/types/common.types";

// constant
import { OUTBOUND } from "@tajawal/web-flights-sdk/lib/constants";

type FareFamiliesProps = {
  selectedFareFamilyItinerary?: ItineraryModel;
  isFlightDetails?: boolean;
  isPaxPage?: boolean;
  title?: string;
  handleCancelChangeClick?: GenericCallback;
  onSelect?: GenericCallback;
  selectedItinerary?: ItineraryModel;
  cardPosition?: number;
};

function FareFamilies({
  selectedFareFamilyItinerary,
  isFlightDetails,
  isPaxPage = false,
  title,
  handleCancelChangeClick,
  onSelect,
  selectedItinerary,
  cardPosition,
}: FareFamiliesProps): JSX.Element {
  const { t } = useTranslation();
  const store = useStore();
  const {
    $fareFamilies,
    $richContent,
    selectItinerary,
    fareFamiliesStore,
    isDomestic,
    currentStep,
    selectedItinerary: selectedItineraryFromState,
    displayItinerary,
    setDisplayItinerary,
    selectedFareFamilyItinerary: selectedFareFamilyItineraryFromState,
    selectFareFamilyItinerary,
    getItinerary,
    $resources,
    isRoundTrip,
    $fare,
    flightInfoStore,
    isOneWay,
    setIsFareFamilyUpgraded,
    isLoadingFareRules,
  } = useStateOfFareFamilies(store, isPaxPage);
  const { itineraryGroupMap } = useStateOfGrouping(store, isRoundTrip);

  const shownItinerary =
    displayItinerary || selectedItineraryFromState || selectedItinerary;
  const _selectedFareFamilyItinerary = isFlightDetails
    ? selectedFareFamilyItineraryFromState
    : selectedFareFamilyItinerary;

  const itinerary = _selectedFareFamilyItinerary;
  const { validatingCarrier, vendor, id } = itinerary || {};
  const isCreditShell = $richContent.isCreditShell(
    validatingCarrier,
    vendor as Vendor,
  );
  const isLoading = $fareFamilies.isFareFamilyLoading(id);
  const hasError = $fareFamilies.didFareFamilyFail(id);
  const fareFamilies = $fareFamilies.getFareFamilies(id);
  const isCompleted = $fareFamilies.didFareFamilyComplete(id);
  // hooks
  useFetchFareFamilies({
    store: fareFamiliesStore,
    itinerary,
    shouldFetch: _isEmpty(fareFamilies),
  });

  const handleReloadFareFamilies = () => {
    fareFamiliesStore.fetch({ itinerary });
  };

  const handleSelectFareFamily = (fareFamilyId, id, index) => {
    if (isPaxPage) {
      onSelect(fareFamilyId);
      return;
    }
    const selectedItinerary = getItinerary(fareFamilyId);
    selectFareFamilyItinerary(id);
    setDisplayItinerary(selectedItinerary);
    if (!isFlightDetails) {
      selectItinerary(fareFamilyId);
    }

    pushStepFlightSelectedEvent({
      itinerary: selectedItinerary,
      index: cardPosition,
      $resources,
      fareFamilyUpgraded: index > 0,
      hasGroup: itineraryGroupMap.has(selectedItinerary.id),
    });
    pushFareFamilySelectedEvent({
      itinerary: selectedItinerary,
      $resources,
      fareFamilyUpgraded: index > 0,
    });
    setIsFareFamilyUpgraded(index > 0, currentStep === OUTBOUND || isOneWay);
  };

  const activeIndex =
    isFlightDetails || isPaxPage
      ? _findIndex(
          fareFamilies,
          (fareFamily) =>
            shownItinerary?.price?.total === fareFamily.totalPrice,
        )
      : -1;

  return (
    <>
      <CardCarousel
        cardWidth={232}
        shouldDarkenArrow={isFlightDetails}
        shouldInitialize={!isLoading && !hasError}
        activeIndex={activeIndex}
        title={
          <Styled.FareFamiliesTitle>
            <Typography
              variant={TYPOGRAPHY_VARIANT.HEADING2}
              component="h1"
              color={
                isFlightDetails
                  ? COLOR_VARIANTS.WHITE
                  : COLOR_VARIANTS.MINESHAFT
              }
            >
              {title || t("FareFamilies.Title")}
            </Typography>
            <Typography
              variant={TYPOGRAPHY_VARIANT.BODY2}
              component="p"
              color={
                isFlightDetails
                  ? COLOR_VARIANTS.AQUA30
                  : COLOR_VARIANTS.GREYCHATEAU
              }
            >
              {t("TimeOptions.Title")}
            </Typography>
          </Styled.FareFamiliesTitle>
        }
        testIdPrefix=""
      >
        <Styled.FareFamiliesWrapper>
          {isLoading && (
            <>
              <FareFamilyCardSkeleton isFlightDetails={isFlightDetails} />
              <FareFamilyCardSkeleton isFlightDetails={isFlightDetails} />
            </>
          )}
          {hasError && !isLoading && (
            <FareFamilyError onReload={handleReloadFareFamilies} />
          )}

          {isCompleted && !isLoading && !hasError && _isEmpty(fareFamilies) && (
            <FareFamilyCardBasic
              isFlightDetails={isFlightDetails}
              isPaxPage={isPaxPage}
              onSelect={() =>
                handleSelectFareFamily(
                  _selectedFareFamilyItinerary.id,
                  _selectedFareFamilyItinerary.id,
                  0,
                )
              }
              itinerary={_selectedFareFamilyItinerary}
              isCreditShell={isCreditShell}
              currentStep={currentStep}
              $fare={$fare}
              flightInfoStore={flightInfoStore}
              isLoading={isLoadingFareRules}
            />
          )}
          {!isLoading && !hasError && (
            <>
              {_map(fareFamilies, (fareFamily, index) => {
                return (
                  <FareFamilyCard
                    key={fareFamily?.code}
                    isSelected={
                      (isFlightDetails || isPaxPage) &&
                      shownItinerary?.price?.total === fareFamily.totalPrice
                    }
                    isFlightDetails={isFlightDetails}
                    getItinerary={getItinerary}
                    index={+index}
                    handleCancelChangeClick={handleCancelChangeClick}
                    fareFamiliesSize={fareFamilies.length - 1}
                    fareFamily={fareFamily}
                    $fareFamilies={$fareFamilies}
                    isPaxPage={isPaxPage}
                    onSelect={() =>
                      handleSelectFareFamily(
                        fareFamily.id,
                        _selectedFareFamilyItinerary.id,
                        index,
                      )
                    }
                    itinerary={_selectedFareFamilyItinerary}
                    isCreditShell={isCreditShell}
                    isDomestic={isDomestic}
                    currentStep={currentStep}
                  />
                );
              })}
            </>
          )}
        </Styled.FareFamiliesWrapper>
      </CardCarousel>
    </>
  );
}

export default FareFamilies;
